import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { repRoundsApi } from '../api/orders/rep-rounds';
import { EmptyState } from './EmptyState';
import { hasPermission } from '../api/auth';

/**
 * Soft gate for /rep/* pages: if user has no linked RegionalRepresentative,
 * show a friendly empty state instead of raw API errors.
 */
export function RepProfileGate({ children }: { children: React.ReactNode }) {
  // A driver is linked to an RP through Driver.executorId. It uses only the
  // pickup, delivery-round and ePOD pages and must not be treated as an
  // unlinked representative account.
  const isRpDriver = hasPermission('rep.rp-rounds.view') && !hasPermission('rep.rounds.view');
  const { data: me, isLoading, isError } = useQuery({
    queryKey: ['rep-rounds-me'],
    queryFn: () => repRoundsApi.me(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (isRpDriver) return <>{children}</>;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh', color: '#666', fontSize: 14 }}>
        Загрузка профиля РП…
      </div>
    );
  }

  const linked = !!(me && (me as { linked?: boolean; id?: string | null }).linked !== false && me.id);

  if (isError || !linked) {
    const canManageReps = hasPermission('reference.view');
    return (
      <div style={{ padding: 24 }}>
        <EmptyState
          asBlock
          title="Профиль РП не привязан"
          description={
            canManageReps
              ? 'У этого пользователя нет карточки исполнителя (RegionalRepresentative). Привяжите портальный логин в разделе «Исполнители», либо откройте админский обзор рейсов РП.'
              : 'К вашей учётной записи не привязан профиль регионального представителя. Обратитесь к администратору.'
          }
        />
        {canManageReps && (
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 16 }}>
            <Link to="/regional-representatives" className="btn btn-primary">Исполнители</Link>
            <Link to="/admin/rp-rounds" className="btn btn-secondary">Обзор рейсов РП</Link>
          </div>
        )}
      </div>
    );
  }

  return <>{children}</>;
}
